// The scripted camera paths the perf harness runs (R431).
//
// A number this file produces must be the same on both clients, so nothing
// here reads a renderer, a layer or a DOM node: a scenario is a pure
// function of the world, the home cell and the owner bytes. `world.ts` and
// `gen/*` are the only imports, and both are engine-agnostic.
//
// The four paths answer four different questions. `far`, `mid` and `near`
// hold one zoom and drift half a pixel a frame, so no layer can latch a
// cached picture and report a cost nobody pays while playing. `pan-borders`
// crosses the busiest ownership border on the map, which is the work the
// territory layer is measured on.

import { COLS, ROWS, cellCenter, neighborAt } from '../world';

/** Every path the harness knows. `standard` runs the other four in order. */
export type ScenarioId = 'far' | 'mid' | 'near' | 'pan-borders' | 'standard';

/** The four paths `standard` is made of, in the order it runs them. */
export const STANDARD: readonly Exclude<ScenarioId, 'standard'>[] =
  ['far', 'mid', 'near', 'pan-borders'];

/** What a path is told about the world it flies over. */
export interface ScenarioCtx {
  /** The world in px, corner to corner. */
  world: { w: number; h: number };
  /** The player's own base, which is where the client starts the camera. */
  home: { x: number; y: number };
  /** The owner byte of every cell, or null before the hello lands. */
  owners: Uint8Array | null;
  cols: number;
  rows: number;
}

/** Where the camera stands for one frame of a path. */
export interface CameraTarget {
  x: number;
  y: number;
  zoom: number;
}

/** One scripted path. */
export interface Scenario {
  id: Exclude<ScenarioId, 'standard'>;
  /** How long the path runs, in wall seconds, before `?perfsec` overrides it. */
  seconds: number;
  /** Where the camera stands at fraction `t` of the run, on frame `frame`. */
  at(ctx: ScenarioCtx, t: number, frame: number): CameraTarget;
}

/**
 * The default length of every path, in wall seconds.
 *
 * Thirty seconds is long enough that a hitch which happens once every few
 * hundred frames still lands inside the window, and short enough that a
 * four-path run takes two minutes. `?perfsec=N` overrides it, so a smoke
 * run of the harness itself takes seconds.
 */
export const SCENARIO_SECONDS = 30;

/**
 * How far a held camera drifts per frame, in world px.
 *
 * A camera that does not move lets every cache in the client stay valid for
 * the whole run, and the number that comes out is the cost of presenting an
 * unchanged picture. Half a pixel a frame moves the viewport without
 * changing what is on screen, so the measured cost is the one a player pays
 * while looking around.
 */
export const DRIFT_PX_PER_FRAME = 0.5;

/**
 * The zoom of each held path.
 *
 * `far` is the far end of the zoom range, `mid` is the zoom the client
 * itself picks when a hello lands, and `near` is the close end, where the
 * map carries the most detail.
 */
export const ZOOM_FAR = 0.25;
export const ZOOM_MID = 0.9;
export const ZOOM_NEAR = 2.5;

/**
 * The zoom `pan-borders` pans at.
 *
 * No zoom band picks it: the engine draws the same layers at every zoom,
 * and the bands of ui-map-interface.md §10.8 retired with PixiJS. 1.2
 * stays because a scenario flies one fixed path, so a number is the same
 * on every run (the rule at the head of this file), and a pan run reads
 * against the baselines recorded at it (`scripts/tools/throughput/README.md`
 * §2).
 */
export const ZOOM_PAN = 1.2;

/** How many cells across the window `densestBorder` looks for. */
export const BORDER_WINDOW_CELLS = 20;

/** The window `densestBorder` found, in cells and in world px. */
export interface BorderWindow {
  /** The window's top-left cell. */
  col: number;
  row: number;
  /** How many cells the window spans on each axis. */
  cells: number;
  /** How many neighbour owner changes the window holds. */
  score: number;
  /** The window in world px, from the first cell's centre to the last one's. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Where the map's ownership borders are densest.
 *
 * The scan is pure and deterministic, so both clients pan over the same
 * ground on the same world: a cell scores one point for every neighbour
 * that another faction owns, and the window with the highest total wins.
 * Unowned water counts as a faction of its own, because a claim edge
 * against open sea is stroked exactly like a claim edge against a rival.
 *
 * A summed-area table makes the window search one pass over the grid, so
 * the whole scan is linear in the number of cells and runs once per path.
 *
 * With no owners — before the hello — the window is the middle of the map,
 * so a path can still run and report a number.
 */
export function densestBorder(
  owners: Uint8Array | null,
  cols = COLS,
  rows = ROWS,
  cells = BORDER_WINDOW_CELLS,
): BorderWindow {
  const span = Math.max(1, Math.min(cells, cols, rows));
  let bestCol = Math.max(0, ((cols - span) / 2) | 0);
  let bestRow = Math.max(0, ((rows - span) / 2) | 0);
  let best = 0;
  if (owners && owners.length >= cols * rows) {
    // One score per cell: how many of its six neighbours belong to somebody
    // else. A cell off the grid is not a neighbour and scores nothing.
    const score = new Float64Array(cols * rows);
    for (let i = 0; i < cols * rows; i++) {
      const own = owners[i];
      let n = 0;
      for (let k = 0; k < 6; k++) {
        const j = neighborAt(i, k, cols, rows);
        if (j >= 0 && owners[j] !== own) n++;
      }
      score[i] = n;
    }
    // Summed-area table, one row and one column of zeros wider than the
    // grid, so a window sum is four reads whatever its size.
    const sw = cols + 1;
    const sum = new Float64Array(sw * (rows + 1));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        sum[(r + 1) * sw + c + 1] = score[r * cols + c]
          + sum[r * sw + c + 1] + sum[(r + 1) * sw + c] - sum[r * sw + c];
      }
    }
    for (let r = 0; r + span <= rows; r++) {
      for (let c = 0; c + span <= cols; c++) {
        const total = sum[(r + span) * sw + c + span] - sum[r * sw + c + span]
          - sum[(r + span) * sw + c] + sum[r * sw + c];
        if (total > best) {
          best = total;
          bestCol = c;
          bestRow = r;
        }
      }
    }
  }
  const [x0, y0] = cellCenter(bestRow * cols + bestCol, cols);
  const [x1, y1] = cellCenter((bestRow + span - 1) * cols + bestCol + span - 1, cols);
  return { col: bestCol, row: bestRow, cells: span, score: best, x0, y0, x1, y1 };
}

/** A held camera at one zoom, drifting so that nothing latches. */
function held(zoom: number): Scenario['at'] {
  return (ctx, _t, frame) => ({
    x: ctx.home.x + frame * DRIFT_PX_PER_FRAME,
    y: ctx.home.y,
    zoom,
  });
}

/**
 * The four paths, by id.
 *
 * `pan-borders` finds its window once per run and caches it against the
 * owner array it was found on, because the scan is linear in the grid and
 * the path asks for it every frame.
 */
let panWindow: BorderWindow | null = null;
let panOwners: Uint8Array | null = null;

export const SCENARIOS: Record<Exclude<ScenarioId, 'standard'>, Scenario> = {
  far: { id: 'far', seconds: SCENARIO_SECONDS, at: held(ZOOM_FAR) },
  mid: { id: 'mid', seconds: SCENARIO_SECONDS, at: held(ZOOM_MID) },
  near: { id: 'near', seconds: SCENARIO_SECONDS, at: held(ZOOM_NEAR) },
  'pan-borders': {
    id: 'pan-borders',
    seconds: SCENARIO_SECONDS,
    at(ctx, t) {
      if (panWindow === null || panOwners !== ctx.owners) {
        panOwners = ctx.owners;
        panWindow = densestBorder(ctx.owners, ctx.cols, ctx.rows);
      }
      // Corner to corner, so the pan crosses the whole window rather than
      // one band of it.
      return {
        x: panWindow.x0 + (panWindow.x1 - panWindow.x0) * t,
        y: panWindow.y0 + (panWindow.y1 - panWindow.y0) * t,
        zoom: ZOOM_PAN,
      };
    },
  },
};

/** The paths one id asks for, in run order. */
export function scenariosOf(id: ScenarioId): Scenario[] {
  if (id === 'standard') return STANDARD.map((s) => SCENARIOS[s]);
  return [SCENARIOS[id]];
}
